import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ArrowUpRight, ChevronDown, ChevronRight } from 'lucide-react';
import { navLinks, site } from '@/data/content';

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileDropdown, setMobileDropdown] = useState<string | null>(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const topLinks = navLinks;
  const desktopLinks = navLinks.filter((l) => l.label !== 'Book a Demo');
  const isActive = (path: string) => pathname === path;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 border-b border-slate-200/80 bg-white/95 backdrop-blur transition-all duration-300 ${
        scrolled ? 'shadow-md' : 'shadow-sm'
      }`}
    >
      <div className='relative mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8'>
        <Link to='/' className='flex items-center gap-2'>
          <img
            src='https://media.getzurihub.com/Jayrah%20Assets/Zurihub.png'
            alt={site.name}
            className='h-9 w-auto object-contain'
          />
          <span className='text-xl font-bold text-slate-900 hidden sm:block'>ZuriHub</span>
        </Link>

        <nav className='hidden md:absolute md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 items-center gap-1 rounded-full border border-slate-200/80 bg-white/70 px-2 py-1.5 backdrop-blur md:flex'>
          {desktopLinks.map((link) =>
            link.children ? (
              <div key={link.label} className='group relative'>
                <Link
                  to={link.path}
                  className='flex items-center gap-1 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900'
                >
                  {link.label}
                  <ChevronDown className='h-4 w-4 transition-transform group-hover:rotate-180' />
                </Link>
                <div className='pointer-events-none absolute left-0 top-full pt-2 opacity-0 transition group-hover:pointer-events-auto group-hover:opacity-100'>
                  <div className='w-72 rounded-2xl border border-slate-200 bg-white p-4 shadow-xl'>
                    {link.children.map((group: any) => (
                      <div key={group.label} className='mb-3 last:mb-0'>
                        <p className='px-3 py-1 text-xs font-bold uppercase text-slate-400'>{group.label}</p>
                        <div className='mt-1 space-y-1'>
                          {group.items.map((item: any) => (
                            <Link
                              key={item.path}
                              to={item.path}
                              className='block rounded-lg px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                            >
                              {item.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <Link
                key={link.path}
                to={link.path}
                className={
                  isActive(link.path)
                    ? 'whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium bg-slate-100 text-slate-900'
                    : 'whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900'
                }
              >
                {link.label}
              </Link>
            )
          )}
        </nav>

        <div className='hidden items-center gap-3 md:flex'>
          <a
            href={site.workspaceUrl}
            target='_blank'
            rel='noreferrer'
            className='group inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white transition-all hover:bg-slate-800'
          >
            Create Workspace
            <ArrowUpRight className='h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5' />
          </a>
        </div>

        <button
          className='flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white/70 text-slate-700 backdrop-blur md:hidden'
          onClick={() => setOpen(!open)}
          aria-label='Toggle menu'
        >
          {open ? <X className='h-5 w-5' /> : <Menu className='h-5 w-5' />}
        </button>
      </div>

      {open && (
        <div className='border-t border-slate-200 bg-white/95 px-4 py-6 shadow-xl backdrop-blur md:hidden'>
          <div className='flex flex-col gap-2'>
            {topLinks.map((link) =>
              link.children ? (
                <div key={link.label} className='flex flex-col gap-1'>
                  <button
                    onClick={() => setMobileDropdown(mobileDropdown === link.label ? null : link.label)}
                    className='flex items-center justify-between rounded-lg px-3 py-2.5 text-base font-medium text-slate-700 hover:bg-slate-100'
                  >
                    {link.label}
                    <ChevronRight className={`h-4 w-4 transition-transform ${mobileDropdown === link.label ? 'rotate-90' : ''}`} />
                  </button>
                  {mobileDropdown === link.label && (
                    <div className='ml-3 flex flex-col gap-2 border-l-2 border-slate-100 pl-3'>
                      {link.children.map((group: any) => (
                        <div key={group.label} className='flex flex-col gap-1'>
                          <p className='px-3 py-1 text-xs font-bold uppercase text-slate-400'>{group.label}</p>
                          {group.items.map((item: any) => (
                            <Link
                              key={item.path}
                              to={item.path}
                              onClick={() => setOpen(false)}
                              className='rounded-lg px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100'
                            >
                              {item.label}
                            </Link>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setOpen(false)}
                  className={
                    isActive(link.path)
                      ? 'rounded-lg px-3 py-2.5 text-base font-medium bg-slate-100 text-slate-900'
                      : 'rounded-lg px-3 py-2.5 text-base font-medium text-slate-700'
                  }
                >
                  {link.label}
                </Link>
              )
            )}
            <a
              href={site.workspaceUrl}
              target='_blank'
              rel='noreferrer'
              onClick={() => setOpen(false)}
              className='mt-2 flex items-center justify-center gap-2 rounded-full bg-slate-900 px-4 py-3 text-center text-sm font-semibold text-white'
            >
              Create Your Workspace <ArrowUpRight className='h-4 w-4' />
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
